import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const tabList = [
  { key: 'details', label: 'Request Details', icon: '📋' },
  { key: 'inspirations', label: 'Inspirations', icon: '🎨' },
  { key: 'messages', label: 'Messages', icon: '💬' },
  { key: 'files', label: 'Files', icon: '📎' },
];

const AdminRequestDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('details');
  const [request, setRequest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [newMessageToast, setNewMessageToast] = useState<string | null>(null);

  useEffect(() => {
    const fetchRequest = async () => {
      setLoading(true);
      
      // First, let's get the request with brand_id
      const { data: requestData, error: requestError } = await supabase
        .from('service_requests')
        .select('*')
        .eq('id', id)
        .single();
      
      if (requestError) {
        console.error('Error fetching request:', requestError);
        setLoading(false);
        return;
      }
      
      console.log('Request data:', requestData);
      console.log('Brand ID:', requestData.brand_id);
      
      // Then, if there's a brand_id, fetch the brand separately
      if (requestData.brand_id) {
        const { data: brandData, error: brandError } = await supabase
          .from('brands')
          .select('id, name, description, logo_url, primary_color, secondary_color, typography, industry, website')
          .eq('id', requestData.brand_id);
        
        if (brandError) {
          console.error('Error fetching brand:', brandError);
        } else {
          console.log('Brand query result:', brandData);
          if (brandData && brandData.length > 0) {
            requestData.brands = brandData[0];
            console.log('Brand data attached:', requestData.brands);
          } else {
            console.log('No brand found for brand_id:', requestData.brand_id);
            requestData.brands = null;
          }
        }
      } else {
        console.log('No brand_id in request');
        requestData.brands = null;
      }
      
      setRequest(requestData);
      setLoading(false);
    };
    if (id) fetchRequest();
  }, [id]);

  useEffect(() => {
    const fetchMessages = async () => {
      const { data, error } = await supabase
        .from('request_message')
        .select('*')
        .eq('request_id', id)
        .order('created_at', { ascending: true });
      if (!error) setMessages(data || []);
    };
    if (id) fetchMessages();
  }, [id]);

  // Mark messages as read when viewing request details
  useEffect(() => {
    if (!id || !user) return;

    const markMessagesAsRead = async () => {
      try {
        // Mark all messages from users (clients) as read for this request
        const { error } = await supabase
          .from('request_message')
          .update({ read: true })
          .eq('request_id', id)
          .eq('sender', 'user')
          .eq('read', false);

        if (!error) {
          console.log('✅ Messages marked as read for request:', id);
        }
      } catch (error) {
        console.error('Error marking messages as read:', error);
      }
    };

    markMessagesAsRead();
  }, [id, user]);

  // Set up real-time subscription for new messages
  useEffect(() => {
    if (!id) return;

    // Subscribe to new messages for this specific request
    const messageSubscription = supabase
      .channel(`request_messages_${id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'request_message',
          filter: `request_id=eq.${id}`
        },
        (payload) => {
          console.log('🔔 New message received in real-time:', payload.new);
          const newMessage = payload.new;
          
          // Show toast notification for new message
          if (newMessage.sender !== 'admin') {
            setNewMessageToast(`New message from ${newMessage.sender === 'user' ? 'user' : 'client'}`);
            setTimeout(() => setNewMessageToast(null), 3000);
          }
          
          // Add the new message to the local state and sort by created_at
          setMessages(prev => {
            const updatedMessages = [...prev, newMessage];
            return updatedMessages.sort((a, b) => 
              new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            );
          });
        }
      )
      .subscribe();

    return () => {
      messageSubscription.unsubscribe();
    };
  }, [id]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!newMessage.trim() && !file) || !user) return;

    let fileUrl = '';
    if (file) {
      const { data: fileData, error: fileError } = await supabase.storage
        .from('messages')
        .upload(`${id}/${file.name}`, file);
      if (!fileError) {
        const { data: { publicUrl } } = supabase.storage
          .from('messages')
          .getPublicUrl(`${id}/${file.name}`);
        fileUrl = publicUrl;
      }
    }

    const { error } = await supabase
      .from('request_message')
      .insert([
        { request_id: id, message: newMessage, sender: 'admin', sender_id: user.id, file_url: fileUrl }
      ]);

    if (!error) {
      setNewMessage('');
      setFile(null);
      // No need to manually refresh messages - real-time subscription will handle it
      console.log('✅ Message sent successfully');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'review':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const renderQuestionsAndAnswers = (request: any) => {
    const qaData = request.questions_and_answers || request.answers;
    
    if (!qaData) {
      return (
        <div className="text-center py-8 text-gray-500">
          <div className="text-4xl mb-2">📝</div>
          <p className="text-lg">No questions and answers available</p>
        </div>
      );
    }

    if (Array.isArray(qaData)) {
      return (
        <div className="space-y-6">
          {qaData.map((item: any, index: number) => {
            // Safely extract question and answer, ensuring they're strings
            let questionText = 'No question provided';
            let answerText = 'No answer provided';
            
            if (item && typeof item === 'object') {
              if (item.question && typeof item.question === 'string') {
                questionText = item.question;
              }
              if (item.answer) {
                if (typeof item.answer === 'string') {
                  answerText = item.answer;
                } else if (typeof item.answer === 'number' || typeof item.answer === 'boolean') {
                  answerText = String(item.answer);
                } else if (Array.isArray(item.answer)) {
                  answerText = item.answer.join(', ');
                } else if (typeof item.answer === 'object' && item.answer !== null) {
                  try {
                    answerText = JSON.stringify(item.answer, null, 2);
                  } catch {
                    answerText = '[Complex Object]';
                  }
                }
              }
            }
            
            // Check if this is a brand assets question or inspiration question
            const isBrandAssetsQuestion = questionText.toLowerCase().includes('brand assets') && Array.isArray(item.answer);
            const isInspirationQuestion = questionText.toLowerCase().includes('inspiration') && Array.isArray(item.answer);
            const isAssetQuestion = isBrandAssetsQuestion || isInspirationQuestion;
            
            return (
              <div key={index} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-semibold text-sm">{index + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">
                      {questionText}
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
                      {isAssetQuestion && Array.isArray(item.answer) && item.answer.length > 0 ? (
                        <div className="space-y-4">
                          <p className="text-gray-700 mb-4">
                            {item.answer.length} {isBrandAssetsQuestion ? 'brand asset' : 'inspiration'}{item.answer.length !== 1 ? 's' : ''} uploaded
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {item.answer.map((url: string, assetIndex: number) => {
                              const isImage = url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);
                              const fileName = url.split('/').pop() || `asset-${assetIndex + 1}`;
                              
                              return (
                                <div key={assetIndex} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                                  {isImage ? (
                                    <div className="relative group">
                                      <img 
                                        src={url} 
                                        alt={`${isBrandAssetsQuestion ? 'Brand Asset' : 'Inspiration'} ${assetIndex + 1}`}
                                        className="w-full h-32 object-cover rounded-lg mb-3"
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          target.style.display = 'none';
                                          const fallbackElement = document.getElementById(`fallback-${assetIndex}`);
                                          if (fallbackElement) {
                                            fallbackElement.classList.remove('hidden');
                                          }
                                        }}
                                      />
                                      <div 
                                        className="hidden w-full h-32 bg-gray-100 rounded-lg mb-3 items-center justify-center"
                                        id={`fallback-${assetIndex}`}
                                      >
                                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                      </div>
                                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300 rounded-lg flex items-center justify-center">
                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                          <svg className="w-8 h-8 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                          </svg>
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="w-full h-32 bg-gray-100 rounded-lg mb-3 flex items-center justify-center">
                                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                    </div>
                                  )}
                                  <div className="text-center">
                                    <p className="text-sm font-medium text-gray-900 truncate mb-2" title={fileName}>
                                      {fileName}
                                    </p>
                                    <div className="flex space-x-2">
                                      <a
                                        href={url}
                                        download={fileName}
                                        className="flex-1 bg-blue-500 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors flex items-center justify-center"
                                      >
                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        Download
                                      </a>
                                      <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bg-gray-500 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-600 transition-colors flex items-center justify-center"
                                        title="Open in new tab"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-700 leading-relaxed">
                          {answerText}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    if (typeof qaData === 'object' && qaData !== null) {
      return (
        <div className="space-y-6">
          {Object.entries(qaData).map(([key, value], index) => {
            // Safely convert any value to a displayable string
            let displayValue = 'No answer provided';
            
            if (value === null || value === undefined) {
              displayValue = 'No answer provided';
            } else if (typeof value === 'string') {
              displayValue = value;
            } else if (typeof value === 'number' || typeof value === 'boolean') {
              displayValue = String(value);
            } else if (Array.isArray(value)) {
              displayValue = value.map(item => {
                if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
                  return String(item);
                }
                return '[Complex Item]';
              }).join(', ');
            } else if (typeof value === 'object' && value !== null) {
              try {
                displayValue = JSON.stringify(value, null, 2);
              } catch {
                displayValue = '[Complex Object]';
              }
            } else {
              displayValue = String(value);
            }

            return (
              <div key={key} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-semibold text-sm">{index + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">
                      {String(key).replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {displayValue}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Fallback for any other data type
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
          <p className="text-gray-700 leading-relaxed">
            {typeof qaData === 'string' ? qaData : String(qaData)}
          </p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading request details...</p>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Request Not Found</h1>
          <p className="text-gray-600 mb-6">The request you're looking for doesn't exist or has been removed.</p>
          <Link to="/admin/requests" className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            ← Back to Requests
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Notification */}
      {newMessageToast && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-pulse">
          <div className="flex items-center space-x-2">
            <span>🔔</span>
            <span>{newMessageToast}</span>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Link 
                  to="/admin/requests" 
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Requests
                </Link>
                <div className="h-6 w-px bg-gray-300"></div>
                <h1 className="text-2xl font-bold text-gray-900">Request Details</h1>
              </div>
              <div className="flex items-center space-x-3">
                {request.brands?.primary_color && (
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-4 h-4 rounded-full border border-gray-200" 
                      style={{ backgroundColor: request.brands.primary_color }}
                      title={`${request.brands.name} Primary Color`}
                    ></div>
                    <div 
                      className="w-4 h-4 rounded-full border border-gray-200" 
                      style={{ backgroundColor: request.brands.secondary_color || '#EF4444' }}
                      title={`${request.brands.name} Secondary Color`}
                    ></div>
                  </div>
                )}
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(request.status)}`}>
                  {request.status?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) || 'Unknown'}
                </span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getPriorityColor(request.priority)}`}>
                  {request.priority?.replace(/\b\w/g, (c: string) => c.toUpperCase()) || 'Medium'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabList.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.key
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                  </span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'details' && (
              <div className="space-y-8">
                {/* Request Overview */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Request Type</h3>
                    <p className="text-lg font-semibold text-gray-900">
                      {request.type?.replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) || 'N/A'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Created</h3>
                    <p className="text-lg font-semibold text-gray-900">
                      {new Date(request.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Last Updated</h3>
                    <p className="text-lg font-semibold text-gray-900">
                      {new Date(request.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {/* Brand Information */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <span className="text-2xl mr-3">🏢</span>
                    Brand Information
                  </h3>
                  {request.brands ? (
                    <div className="space-y-6">
                      {/* Brand Header with Logo */}
                      <div className="flex items-start space-x-4">
                        {request.brands.logo_url ? (
                          <div className="w-20 h-20 bg-white rounded-xl border border-gray-200 flex items-center justify-center overflow-hidden">
                            <img 
                              src={request.brands.logo_url} 
                              alt={`${request.brands.name} logo`}
                              className="w-full h-full object-contain"
                            />
                          </div>
                        ) : (
                          <div className="w-20 h-20 bg-blue-100 rounded-xl flex items-center justify-center">
                            <span className="text-blue-600 text-2xl font-bold">
                              {request.brands.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="flex-1">
                          <h4 className="text-2xl font-bold text-gray-900 mb-2">{request.brands.name}</h4>
                          {request.brands.description && (
                            <p className="text-gray-600 text-lg mb-3">{request.brands.description}</p>
                          )}
                          {request.brands.industry && (
                            <div className="flex items-center space-x-2">
                              <span className="text-gray-500">🏭</span>
                              <span className="text-gray-700 font-medium">{request.brands.industry}</span>
                            </div>
                          )}
                          {request.brands.website && (
                            <div className="flex items-center space-x-2 mt-2">
                              <span className="text-gray-500">🌐</span>
                              <a 
                                href={request.brands.website} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 underline font-medium"
                              >
                                {request.brands.website}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Brand Visual Identity */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Brand Colors */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                            <span className="text-lg mr-2">🎨</span>
                            Brand Colors
                          </h5>
                          <div className="space-y-3">
                            {request.brands.primary_color && (
                              <div className="flex items-center space-x-3">
                                <div 
                                  className="w-8 h-8 rounded-lg border border-gray-200 shadow-sm" 
                                  style={{ backgroundColor: request.brands.primary_color }}
                                ></div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Primary</p>
                                  <p className="text-xs text-gray-500 font-mono">{request.brands.primary_color}</p>
                                </div>
                              </div>
                            )}
                            {request.brands.secondary_color && (
                              <div className="flex items-center space-x-3">
                                <div 
                                  className="w-8 h-8 rounded-lg border border-gray-200 shadow-sm" 
                                  style={{ backgroundColor: request.brands.secondary_color }}
                                ></div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Secondary</p>
                                  <p className="text-xs text-gray-500 font-mono">{request.brands.secondary_color}</p>
                                </div>
                              </div>
                            )}
                            {!request.brands.primary_color && !request.brands.secondary_color && (
                              <p className="text-sm text-gray-500 italic">No brand colors specified</p>
                            )}
                          </div>
                        </div>

                        {/* Typography */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                            <span className="text-lg mr-2">🔤</span>
                            Typography
                          </h5>
                          {request.brands.typography ? (
                            <div>
                              <p className="text-sm font-medium text-gray-900 mb-1">Primary Font</p>
                              <p className="text-sm text-gray-700 font-mono bg-white px-3 py-2 rounded border">
                                {request.brands.typography}
                              </p>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic">No typography specified</p>
                          )}
                        </div>
                      </div>

                      {/* Brand Assets */}
                      {request.brands.logo_url && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                            <span className="text-lg mr-2">📎</span>
                            Brand Assets
                          </h5>
                          <div className="flex items-center space-x-3">
                            <div className="w-16 h-16 bg-white rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden">
                              <img 
                                src={request.brands.logo_url} 
                                alt={`${request.brands.name} logo`}
                                className="w-full h-full object-contain"
                              />
                            </div>
                            <div>
                              <p className="text-sm text-gray-900">Brand Logo</p>
                              <a 
                                href={request.brands.logo_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 text-sm underline"
                              >
                                View full size
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : request.brand_id ? (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-3">⚠️</div>
                      <h4 className="text-lg font-medium text-amber-800 mb-2">Brand Not Found</h4>
                      <p className="text-sm text-gray-600 mb-3">This brand may have been deleted or never created.</p>
                      <div className="bg-gray-100 rounded-lg p-3 inline-block">
                        <span className="text-xs font-mono text-gray-600">Brand ID: {request.brand_id}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-3">📝</div>
                      <p className="text-gray-500">No brand associated with this request</p>
                    </div>
                  )}
                </div>

                {/* Questions and Answers */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <span className="text-2xl mr-3">❓</span>
                    Questions & Answers
                  </h3>
                  {renderQuestionsAndAnswers(request)}
                </div>
              </div>
            )}

            {activeTab === 'inspirations' && (
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Inspiration Images & Examples</h3>
                  {(() => {
                    // Extract inspiration URLs from questions_and_answers
                    let inspirationUrls: string[] = [];
                    
                    if (request.questions_and_answers) {
                      if (Array.isArray(request.questions_and_answers)) {
                        // Find the inspiration question
                        const inspirationQuestion = request.questions_and_answers.find((item: any) => 
                          item.question && item.question.includes('Upload inspiration images or examples')
                        );
                        if (inspirationQuestion && Array.isArray(inspirationQuestion.answer)) {
                          inspirationUrls = inspirationQuestion.answer;
                        }
                      } else if (typeof request.questions_and_answers === 'object') {
                        // Check if inspirations are stored directly
                        if (request.questions_and_answers.inspirations && Array.isArray(request.questions_and_answers.inspirations)) {
                          inspirationUrls = request.questions_and_answers.inspirations;
                        }
                      }
                    }
                    
                    // Fallback to check the answers field
                    if (inspirationUrls.length === 0 && request.answers) {
                      if (request.answers.inspirations && Array.isArray(request.answers.inspirations)) {
                        inspirationUrls = request.answers.inspirations;
                      }
                    }
                    
                    return (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {inspirationUrls.length > 0 ? (
                          inspirationUrls.map((inspiration: string, index: number) => (
                            <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
                              <div className="relative overflow-hidden rounded-lg mb-3">
                                <img 
                                  src={inspiration} 
                                  alt={`Inspiration ${index + 1}`} 
                                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300" 
                                />
                                {/* Image overlay with zoom indicator */}
                                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all duration-300 flex items-center justify-center">
                                  <div className="opacity-0 hover:opacity-100 transition-opacity duration-300">
                                    <svg className="w-8 h-8 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                              <div className="text-center">
                                <p className="text-sm text-gray-600 truncate mb-2">{inspiration.split('/').pop()}</p>
                                <a 
                                  href={inspiration} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 transition-colors"
                                >
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                  View Full Size
                                </a>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-8 text-gray-500 col-span-full">
                            <div className="text-4xl mb-2">🎨</div>
                            <p className="text-lg font-medium text-gray-600 mb-2">No Inspiration Images Uploaded</p>
                            <p className="text-sm text-gray-500">The client hasn't uploaded any inspiration images or examples for this request yet.</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {activeTab === 'messages' && (
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversation History</h3>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {messages.length > 0 ? (
                      messages.map((msg) => (
                        <div key={msg.id} className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl ${
                            msg.sender === 'admin' 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-white border border-gray-200 text-gray-900'
                          }`}>
                            <div className="text-sm">{msg.message}</div>
                            {msg.file_url && (
                              <div className="mt-2">
                                {msg.file_url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                  <img src={msg.file_url} alt="attachment" className="max-w-xs max-h-40 rounded" />
                                ) : (
                                  <a href={msg.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Download File</a>
                                )}
                              </div>
                            )}
                            <div className={`text-xs mt-2 ${msg.sender === 'admin' ? 'text-blue-100' : 'text-gray-500'}`}>
                              {new Date(msg.created_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <div className="text-4xl mb-2">💬</div>
                        <p>No messages yet. Start the conversation!</p>
                      </div>
                    )}
                  </div>
                </div>

                <form onSubmit={handleSendMessage} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-end space-x-3">
                    <div className="flex-1">
                      <textarea
                        value={newMessage}
                        onChange={e => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      />
                    </div>
                    <div className="flex flex-col space-y-2">
                      <input
                        type="file"
                        onChange={e => setFile(e.target.files?.[0] || null)}
                        className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      <button 
                        type="submit" 
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        Send
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            )}

            {activeTab === 'files' && (
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">File Attachments</h3>
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      if (!file || !user) return;
                      let fileUrl = '';
                      const { data: fileData, error: fileError } = await supabase.storage
                        .from('messages')
                        .upload(`${id}/${file.name}`, file);
                      if (!fileError) {
                        const { data: { publicUrl } } = supabase.storage
                          .from('messages')
                          .getPublicUrl(`${id}/${file.name}`);
                        fileUrl = publicUrl;
                        await supabase.from('request_message').insert([
                          { request_id: id, message: '', sender: 'admin', sender_id: user.id, file_url: fileUrl }
                        ]);
                        // No need to manually refresh messages - real-time subscription will handle it
                        setFile(null);
                        console.log('✅ File uploaded successfully');
                      }
                    }}
                    className="flex items-center space-x-3 mb-6"
                  >
                    <input
                      type="file"
                      onChange={e => setFile(e.target.files?.[0] || null)}
                      className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium">
                      Upload File
                    </button>
                  </form>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {messages.filter(msg => msg.file_url).map(msg => (
                      <div key={msg.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                        {msg.file_url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                          <img src={msg.file_url} alt="attachment" className="w-full h-32 object-cover rounded mb-3" />
                        ) : (
                          <div className="w-full h-32 bg-gray-100 rounded mb-3 flex items-center justify-center">
                            <span className="text-4xl text-gray-400">📄</span>
                          </div>
                        )}
                        <div className="text-center">
                          <p className="text-sm text-gray-600 mb-2 truncate">
                            {msg.file_url.split('/').pop()}
                          </p>
                          <a 
                            href={msg.file_url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 transition-colors"
                          >
                            Download
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminRequestDetails; 